import {
  Table, TableHeader, Column, TableBody, Row, Cell, Button,
} from 'react-aria-components';
import { Fragment, ReactNode } from 'react';
import customIcons from '../utils/icons';
import getVarianceStyle from '../utils/varianceStyle';

interface ExpandTableColumn {
  key: string;
  label: string;
}

interface ExpandTableRow {
  id: string;
  data: Record<string, string>;
  trends?: Record<string, string | undefined>;
  detail?: ReactNode;
  loading?: boolean;
}

interface ExpandTableProps {
  label: string;
  columns: ExpandTableColumn[];
  rows: ExpandTableRow[];
  expandedRow?: string;
  onExpandedRowChange: (id: string | undefined) => void;
}

interface ExpandTableCellContentProps {
  value: string;
  trend?: string;
  loading?: boolean;
}

function ExpandTableCellContent({ value, trend, loading }: ExpandTableCellContentProps) {
  if (loading) return <div className="h-4 rounded bg-gray-200" />;
  if (trend) {
    const { textTrend } = getVarianceStyle(value, trend);
    return <span className={textTrend}>{value}</span>;
  }
  return value;
}

export default function ExpandTable({
  label, columns, rows, expandedRow, onExpandedRowChange
}: ExpandTableProps) {
  const hasDetails = rows.some((row) => row.detail);

  const toggleRow = (id: string) => {
    onExpandedRowChange(id === expandedRow ? undefined : id);
  };

  return (
    <Table
      aria-label={label}
      className="w-full table-fixed rounded-xl border border-pipgrey-border bg-white shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
    >
      <TableHeader className="bg-pipbg-subtle">
        {hasDetails && <Column className="w-12" />}
        {columns.map((col, index) => (
          <Column
            key={col.key}
            isRowHeader={index === 0}
            className={`px-4 py-3 font-nunito text-[15px] font-bold leading-[21px] text-secondary ${
              index === 0 ? 'text-left' : 'text-right'
            }`}
          >
            {col.label}
          </Column>
        ))}
      </TableHeader>
      <TableBody>
        {rows.map((item) => {
          const rowBg = !item.loading && expandedRow === item.id ? 'bg-table-row-select' : 'bg-table-default';
          const rowClassName = `border-b border-table-border ${item.loading ? 'animate-pulse' : ''} ${rowBg}`;

          return (
            <Fragment key={item.id}>
              <Row className={rowClassName}>
                {hasDetails && (
                  <Cell className="flex h-12 w-12 items-center justify-center">
                    {!item.loading && (
                      <Button className="text-primary" onPress={() => toggleRow(item.id)}>
                        {expandedRow === item.id ? customIcons.chevronDown : customIcons.chevronRight}
                      </Button>
                    )}
                  </Cell>
                )}
                {columns.map((col, index) => (
                  <Cell
                    key={col.key}
                    className={`px-4 font-nunito text-sm leading-[21px] text-secondary ${
                      index === 0 ? 'text-left py-2' : 'text-right py-3'
                    }`}
                  >
                    <ExpandTableCellContent
                      value={item.data[col.key]}
                      trend={item.trends?.[col.key]}
                      loading={item.loading && index !== 0 ? true : undefined}
                    />
                  </Cell>
                ))}
              </Row>
              {!item.loading && hasDetails && expandedRow === item.id && item.detail && (
                <Row className="border-b border-table-border">
                  <Cell />
                  <Cell colSpan={columns.length}>{item.detail}</Cell>
                </Row>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
